import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { aiVisibilityQuestions, aiVisibilityRun, aiVisibilityRuns } from "../../services/api";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, XCircle, AlertTriangle, Copy, ExternalLink, ChevronDown, Loader2 } from "lucide-react";

const Cell = ({ r, onClick }) => {
  if (!r) return <td className="p-2 text-center text-ink/30">–</td>;
  const icon = r.error ? <AlertTriangle className="w-5 h-5 text-amber-500 mx-auto" /> : r.mentioned ? <CheckCircle2 className="w-5 h-5 text-green-600 mx-auto" /> : <XCircle className="w-5 h-5 text-red-400 mx-auto" />;
  return <td className="p-2 text-center cursor-pointer hover:bg-cream" onClick={onClick} title={r.error || r.excerpt}>{icon}</td>;
};

export const AdminAiVisibility = () => {
  const [meta, setMeta] = useState(null);
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [openQ, setOpenQ] = useState(null);

  useEffect(() => {
    aiVisibilityQuestions().then(setMeta).catch(() => {});
    aiVisibilityRuns().then(setRuns).catch(() => {});
  }, []);

  const run = async () => {
    setRunning(true);
    try {
      const doc = await aiVisibilityRun();
      setRuns((prev) => [doc, ...prev].slice(0, 10));
      const total = Object.values(doc.summary).reduce((a, s) => a + s.mentioned, 0);
      toast.success(`Test abgeschlossen: ${total}/30 Antworten nennen My Own Cuvée`);
    } catch {
      toast.error("Testlauf fehlgeschlagen — bitte erneut versuchen");
    }
    setRunning(false);
  };

  const copyQ = (q) => {
    navigator.clipboard.writeText(q);
    toast.success("Frage kopiert");
  };

  const latest = runs[0];
  const models = meta?.models?.map((m) => m.label) || [];
  const questions = meta?.questions || [];
  const byQ = (qid, label) => latest?.results?.find((r) => r.qid === qid && r.model === label);

  return (
    <div className="space-y-6" data-testid="admin-ai-visibility">
      <div className="bg-white rounded-2xl border border-border p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-serif text-xl text-ink flex items-center gap-2"><Sparkles className="w-5 h-5 text-gold" /> KI-Sichtbarkeits-Test</h3>
            <p className="text-sm text-ink/55 mt-1 max-w-2xl">
              Stellt 10 realistische Kundenfragen parallel an GPT, Claude und Gemini und prüft, ob „My Own Cuvée" genannt wird.
              Misst die Verankerung im Modell-Wissen (ohne Live-Websuche). Nutzt deinen Universal Key — Kosten pro Lauf im Rappen-Bereich.
            </p>
          </div>
          <Button data-testid="ai-visibility-run-btn" onClick={run} disabled={running} className="bg-wine hover:bg-wine-dark text-white rounded-full h-11 px-6 gap-2 shrink-0">
            {running ? <><Loader2 className="w-4 h-4 animate-spin" /> Läuft… (~1 Min.)</> : <><Sparkles className="w-4 h-4" /> Test starten</>}
          </Button>
        </div>

        {latest && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            {models.map((label) => {
              const s = latest.summary[label] || { mentioned: 0, total: 10, errors: 0 };
              const pct = s.total ? Math.round((s.mentioned / s.total) * 100) : 0;
              return (
                <div key={label} className="rounded-xl border border-border bg-cream p-4" data-testid={`ai-score-${label.split(" ")[0].toLowerCase()}`}>
                  <p className="text-xs uppercase tracking-wider text-ink/50">{label}</p>
                  <p className="text-2xl font-serif text-ink mt-1">{s.mentioned}/{s.total} <span className={`text-sm ${pct >= 50 ? "text-green-600" : "text-red-400"}`}>({pct}%)</span></p>
                  <p className="text-xs text-ink/45 mt-0.5">Brand genannt{s.errors ? ` · ${s.errors} Fehler` : ""}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-cream-dark text-ink/60">
            <tr>
              <th className="text-left p-3 font-medium">Kundenfrage</th>
              {models.map((m) => <th key={m} className="p-3 font-medium w-28">{m}</th>)}
              <th className="p-3 font-medium w-36">Manuell prüfen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {questions.map((q) => (
              <React.Fragment key={q.id}>
                <tr data-testid={`ai-row-${q.id}`}>
                  <td className="p-3 text-ink/80">
                    <button className="flex items-start gap-1.5 text-left hover:text-wine" onClick={() => setOpenQ(openQ === q.id ? null : q.id)}>
                      <ChevronDown className={`w-4 h-4 mt-0.5 shrink-0 transition-transform ${openQ === q.id ? "rotate-180" : ""}`} /> {q.q}
                    </button>
                  </td>
                  {models.map((m) => <Cell key={m} r={byQ(q.id, m)} onClick={() => setOpenQ(openQ === q.id ? null : q.id)} />)}
                  <td className="p-2">
                    <div className="flex items-center justify-center gap-1">
                      <button title="Frage kopieren" className="p-1.5 rounded hover:bg-cream text-ink/50 hover:text-wine" onClick={() => copyQ(q.q)}><Copy className="w-4 h-4" /></button>
                      <a title="In ChatGPT testen" className="p-1.5 rounded hover:bg-cream text-ink/50 hover:text-wine" href={`https://chatgpt.com/?q=${encodeURIComponent(q.q)}`} target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4" /></a>
                      <a title="In Perplexity testen" className="p-1.5 rounded hover:bg-cream text-xs font-semibold text-ink/50 hover:text-wine" href={`https://www.perplexity.ai/search?q=${encodeURIComponent(q.q)}`} target="_blank" rel="noreferrer">P</a>
                    </div>
                  </td>
                </tr>
                {openQ === q.id && latest && (
                  <tr className="bg-cream/60">
                    <td colSpan={models.length + 2} className="p-4 space-y-2">
                      {models.map((m) => {
                        const r = byQ(q.id, m);
                        return (
                          <p key={m} className="text-xs text-ink/65">
                            <span className="font-semibold text-ink/80">{m}:</span> {r?.error ? <span className="text-amber-600">Fehler: {r.error}</span> : r?.excerpt || "—"}
                          </p>
                        );
                      })}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {runs.length > 1 && (
        <div className="bg-white rounded-2xl border border-border p-5">
          <h4 className="font-medium text-ink mb-3">Verlauf</h4>
          <div className="space-y-1.5">
            {runs.map((r) => {
              const total = Object.values(r.summary || {}).reduce((a, s) => a + s.mentioned, 0);
              const max = Object.values(r.summary || {}).reduce((a, s) => a + s.total, 0);
              return (
                <p key={r.id} className="text-sm text-ink/60 flex justify-between border-b border-border/60 pb-1.5">
                  <span>{new Date(r.created_at).toLocaleString("de-CH")}</span>
                  <span className={total / max >= 0.5 ? "text-green-600" : "text-ink/70"}>{total}/{max} Nennungen</span>
                </p>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
